/**
 * A small typed fetch client for contract/openapi.yaml.
 *
 * - Paths, path/query params, request bodies and responses are typed from the generated
 *   `schema.ts` (`pnpm gen:api`).
 * - The access token lives in memory only. The refresh token is the httpOnly cookie the API
 *   sets for `X-MyDash-Client: web`; auth calls send that header and `credentials: 'include'`.
 * - A missing token or a 401 triggers one silent refresh (single-flight, and serialised across
 *   tabs with the Web Locks API so two tabs never spend the same rotating refresh token), then
 *   the request is retried once. A failed refresh clears the session; the app routes to sign-in.
 */
import { API_URL } from '@/config';
import type { paths } from './schema';
import type { ApiErrorBody, AuthResponse } from './types';

export type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

export function isApiError(e: unknown, status?: number): e is ApiError {
  return e instanceof ApiError && (status === undefined || e.status === status);
}

/** A readable message for any thrown value. */
export function errorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

// ------------------------------------------------------------------ session

let accessToken: string | null = null;
type SessionListener = (auth: AuthResponse | null) => void;
const listeners = new Set<SessionListener>();

export function getAccessToken(): string | null {
  return accessToken;
}

/** Store (or clear) the session and tell subscribers (the AuthProvider). */
export function setSession(auth: AuthResponse | null): void {
  accessToken = auth?.access_token ?? null;
  for (const l of listeners) l(auth);
}

export function onSessionChange(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const AUTH_HEADERS = { 'X-MyDash-Client': 'web' } as const;

let refreshInFlight: Promise<AuthResponse | null> | null = null;

/** POST /v1/auth/refresh with the cookie. Concurrent callers share one request. */
export function refreshSession(): Promise<AuthResponse | null> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<AuthResponse | null> {
  const run = async (): Promise<AuthResponse | null> => {
    const res = await fetch(`${API_URL}/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...AUTH_HEADERS, 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!res.ok) return null;
    return (await res.json()) as AuthResponse;
  };
  let auth: AuthResponse | null = null;
  try {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    auth = locks ? await locks.request('mydash-refresh', run) : await run();
  } catch {
    auth = null;
  }
  setSession(auth);
  return auth;
}

// ------------------------------------------------------------------ typed request

type PathsWith<M extends Method> = {
  [P in keyof paths]: paths[P] extends { [K in M]: object } ? P : never;
}[keyof paths];

type Op<P extends keyof paths, M extends Method> = NonNullable<paths[P][M]>;
type Params<O> = O extends { parameters: infer X } ? X : object;

type PathOpt<O> = Params<O> extends { path: infer X } ? { path: X } : { path?: undefined };
type QueryOpt<O> = Params<O> extends { query?: infer Q } ? ([Q] extends [never] ? { query?: undefined } : { query?: Q }) : { query?: undefined };
type BodyOpt<O> = O extends { requestBody: { content: { 'application/json': infer B } } }
  ? { body: B }
  : O extends { requestBody?: { content: { 'application/json': infer B } } }
    ? { body?: B }
    : { body?: undefined };

type JsonOf<X> = X extends { content: { 'application/json': infer J } } ? J : void;
type Success<O> = O extends { responses: infer R }
  ? 200 extends keyof R
    ? JsonOf<R[200]>
    : 201 extends keyof R
      ? JsonOf<R[201]>
      : 202 extends keyof R
        ? JsonOf<R[202]>
        : void
  : never;

export type RequestOptions<O> = PathOpt<O> & QueryOpt<O> & BodyOpt<O> & { signal?: AbortSignal };
type RequiredKeys<T> = { [K in keyof T]-?: object extends Pick<T, K> ? never : K }[keyof T];
type Args<O> = [RequiredKeys<RequestOptions<O>>] extends [never] ? [opts?: RequestOptions<O>] : [opts: RequestOptions<O>];

export type ResponseOf<P extends keyof paths, M extends Method> = Success<Op<P, M>>;

/** Endpoints that must carry the web client header and the refresh cookie. */
const COOKIE_PATHS = new Set(['/v1/auth/register', '/v1/auth/login', '/v1/auth/refresh', '/v1/auth/logout']);
/** Endpoints that don't take a bearer token. */
const PUBLIC_PATHS = new Set([...COOKIE_PATHS, '/v1/auth/password/forgot', '/v1/auth/password/reset', '/health']);

interface RawOptions {
  path?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
}

export function buildUrl(template: string, opts: RawOptions = {}): string {
  const path = template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const v = opts.path?.[key];
    if (v === undefined) throw new Error(`Missing path parameter ${key} for ${template}`);
    return encodeURIComponent(v);
  });
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null) qs.set(k, String(v));
  }
  const q = qs.toString();
  return `${API_URL}${path}${q ? `?${q}` : ''}`;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  const e = body?.error;
  const fallback =
    res.status === 401
      ? 'Please sign in again.'
      : res.status === 403
        ? "You don't have access to that."
        : res.status === 404
          ? "We couldn't find that."
          : res.status === 429
            ? 'Too many tries. Wait a minute and try again.'
            : 'Something went wrong. Please try again.';
  return new ApiError(res.status, e?.code ?? `http_${res.status}`, e?.message || fallback, e?.fields ?? {});
}

async function rawRequest(method: Method, template: string, opts: RawOptions = {}, retried = false): Promise<unknown> {
  const cookie = COOKIE_PATHS.has(template);
  const needsAuth = !PUBLIC_PATHS.has(template);

  if (needsAuth && !accessToken && !(await refreshSession())) {
    throw new ApiError(401, 'unauthorized', 'Please sign in.');
  }
  const tokenUsed = accessToken;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (cookie) Object.assign(headers, AUTH_HEADERS);
  if (needsAuth && tokenUsed) headers.Authorization = `Bearer ${tokenUsed}`;

  let res: Response;
  try {
    res = await fetch(buildUrl(template, opts), {
      method: method.toUpperCase(),
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      credentials: cookie ? 'include' : 'same-origin',
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ApiError(0, 'network', "Can't reach MyDash right now. Check your connection and try again.");
  }

  if (res.status === 401 && needsAuth && !retried) {
    // Another request may already have refreshed while this one was in flight.
    const fresh = accessToken && accessToken !== tokenUsed ? accessToken : (await refreshSession())?.access_token;
    if (fresh) return rawRequest(method, template, opts, true);
    throw await parseError(res);
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204 || res.headers.get('content-length') === '0') return undefined;
  const text = await res.text();
  return text ? (JSON.parse(text) as unknown) : undefined;
}

function method<M extends Method>(m: M) {
  return <P extends PathsWith<M>>(path: P, ...args: Args<Op<P, M>>): Promise<Success<Op<P, M>>> =>
    rawRequest(m, path as string, (args[0] ?? {}) as RawOptions) as Promise<Success<Op<P, M>>>;
}

export const api = {
  get: method('get'),
  post: method('post'),
  put: method('put'),
  patch: method('patch'),
  delete: method('delete'),
};
